import React from 'react';
import { TextField, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { radius, colors } from 'common/theme';
import { FontFamily, typographyScale, TypographyVariant } from 'common/theme';

const StyledOtpInput = styled(TextField)(({ theme, focused, isMobile, isTablet, hasError }) => ({
    width: isMobile ? 42 : isTablet ? 46 : 48,
    direction: 'ltr',
    unicodeBidi: 'bidi-override',
    '& .MuiOutlinedInput-root': {
        borderRadius: radius.md,
        height: isMobile ? 48 : isTablet ? 52 : 56,
        direction: 'ltr',
        unicodeBidi: 'bidi-override',
        backgroundColor: colors.palette.white,
        transition: 'all 0.2s ease',
        '& fieldset': {
            borderWidth: focused ? 2 : 1,
            borderColor: hasError 
                ? theme.palette.error.main 
                : focused 
                    ? colors.palette.blue550 
                    : colors.palette.blueAlpha200,
            transition: 'all 0.2s ease',
        },
        '& input': {
            padding: 0,
            textAlign: 'center',
            direction: 'ltr',
            unicodeBidi: 'bidi-override',
            fontSize: typographyScale[TypographyVariant.TitleLarge].fontSize,
            fontFamily: FontFamily.regular,
            color: theme.palette.text.primary,
        },
        '&:hover': {
            '& fieldset': {
                borderColor: hasError ? theme.palette.error.main : colors.palette.blue550,
            },
        },
        '&.Mui-focused': {
            '& fieldset': {
                borderWidth: 2,
                borderColor: hasError ? theme.palette.error.main : colors.palette.blue550,
            },
            boxShadow: hasError 
                ? `0 0 0 3px ${theme.palette.error.light}30`
                : `0 0 0 3px ${colors.palette.blue550}20`,
        },
    },
    '& input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
        WebkitTextFillColor: 'inherit !important',
    },
    '& input:-webkit-autofill:hover': {
        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
    },
    '& input:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
    },
    '& input:-webkit-autofill:active': {
        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
    },
}));

export const OtpInputHot = ({ 
    index, 
    visualIndex, 
    focusedIndex, 
    inputProps, 
    onInput,
    onPaste,
    testID,
    error,
    ...props 
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
    const isTablet = useMediaQuery(theme.breakpoints.between('tablet', 'desktop_large'));

    const defaultInputProps = {
        maxLength: index === 0 ? 6 : 1,
        inputMode: 'numeric',
        pattern: '[0-9]',
        autoComplete: 'one-time-code',
        tabIndex: index + 1,
        enterKeyHint: index === 5 ? 'done' : 'next',
        style: { 
            textAlign: 'center',
            fontFamily: FontFamily.regular,
            direction: 'ltr',
            unicodeBidi: 'bidi-override'
        },
        onInput: onInput,
        onPaste: onPaste
    };

    const mergedInputProps = {
        ...defaultInputProps,
        ...inputProps,
        style: {
            ...defaultInputProps.style,
            ...(inputProps?.style || {})
        },
        onInput: onInput || inputProps?.onInput,
        onPaste: onPaste || inputProps?.onPaste
    };

    return (
        <StyledOtpInput
            {...props}
            testId={testID}
            focused={focusedIndex === index}
            isMobile={isMobile}
            isTablet={isTablet}
            hasError={error}
            inputProps={mergedInputProps}
            sx={{
                order: visualIndex,
                ...props.sx,
            }}
        />
    );
};

export default OtpInputHot;
